<template>
    <div class="content">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">
                        {{ $t("sync.log.title") }}
                    </h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-bread-crumb></route-bread-crumb>
                    </nav>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <card class="no-border-card" body-classes="px-0 pb-1">
                <template slot="header">
                    <div class="row align-items-center">
                        <div class="col-12">
                            <h3 class="mb-0">{{ $t("sync.log.title") }}</h3>
                        </div>
                    </div>
                </template>

                <!-- ============ Filters ============ -->
                <div class="px-4 pb-3">
                    <div class="row">
                        <div class="col-md-2 mb-2">
                            <label class="form-control-label">{{ $t("sync.log.filter_platform") }}</label>
                            <el-select v-model="filters.platform" clearable style="width: 100%">
                                <el-option :label="$t('sync.log.all')" value=""></el-option>
                                <el-option
                                    v-for="p in platformOptions"
                                    :key="p.value"
                                    :label="p.label"
                                    :value="p.value"
                                ></el-option>
                            </el-select>
                        </div>
                        <div class="col-md-2 mb-2">
                            <label class="form-control-label">{{ $t("sync.log.filter_type") }}</label>
                            <el-select v-model="filters.type" clearable style="width: 100%">
                                <el-option :label="$t('sync.log.all')" value=""></el-option>
                                <el-option :label="$t('sync.log.type_order')" value="order"></el-option>
                                <el-option :label="$t('sync.log.type_product')" value="product"></el-option>
                            </el-select>
                        </div>
                        <div class="col-md-2 mb-2">
                            <label class="form-control-label">{{ $t("sync.log.filter_status") }}</label>
                            <el-select v-model="filters.status" clearable style="width: 100%">
                                <el-option :label="$t('sync.log.all')" value=""></el-option>
                                <el-option
                                    v-for="s in statusOptions"
                                    :key="s"
                                    :label="$t('sync.modal.status.' + s)"
                                    :value="s"
                                ></el-option>
                            </el-select>
                        </div>
                        <div class="col-md-3 mb-2">
                            <label class="form-control-label">{{ $t("sync.log.filter_date_from") }}</label>
                            <el-date-picker
                                v-model="filters.time_from"
                                type="datetime"
                                value-format="yyyy-MM-dd HH:mm:ss"
                                :clearable="true"
                                style="width: 100%"
                            ></el-date-picker>
                        </div>
                        <div class="col-md-3 mb-2">
                            <label class="form-control-label">{{ $t("sync.log.filter_date_to") }}</label>
                            <el-date-picker
                                v-model="filters.time_to"
                                type="datetime"
                                value-format="yyyy-MM-dd HH:mm:ss"
                                :clearable="true"
                                style="width: 100%"
                            ></el-date-picker>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-12 text-right">
                            <base-button size="sm" type="primary" @click="search">
                                <i class="fa fa-search"></i>
                                {{ $t("sync.log.search") }}
                            </base-button>
                        </div>
                    </div>
                </div>

                <!-- ============ Table ============ -->
                <div v-loading="loading">
                    <el-table
                        :data="rows"
                        row-key="job_id"
                        header-row-class-name="thead-light"
                        :empty-text="$t('sync.log.no_data')"
                    >
                        <el-table-column :label="$t('sync.log.col_created_at')" min-width="160px">
                            <template slot-scope="{ row }">
                                {{ formatTime(row.created_at) }}
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('sync.log.col_platform')" prop="platform" min-width="110px"></el-table-column>
                        <el-table-column :label="$t('sync.log.col_type')" min-width="100px">
                            <template slot-scope="{ row }">
                                {{ typeLabel(row.type) }}
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('sync.log.col_shop')" prop="shop_name" min-width="140px">
                            <template slot-scope="{ row }">
                                {{ row.shop_name || "-" }}
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('sync.log.col_created_by')" min-width="130px">
                            <template slot-scope="{ row }">
                                {{ row.created_by_name || "-" }}
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('sync.log.col_status')" min-width="110px">
                            <template slot-scope="{ row }">
                                <el-tag :type="statusTagType(row.status)" size="small">
                                    {{ statusLabel(row.status) }}
                                </el-tag>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('sync.log.col_progress')" min-width="120px">
                            <template slot-scope="{ row }">
                                <span>{{ row.done || 0 }} / {{ row.total || 0 }}</span>
                                <span v-if="row.failed > 0" class="text-danger ml-1">
                                    ({{ row.failed }})
                                </span>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('sync.log.col_error')" min-width="160px">
                            <template slot-scope="{ row }">
                                <el-tooltip
                                    v-if="row.error"
                                    :content="row.error"
                                    placement="top"
                                    effect="dark"
                                >
                                    <span class="sync-log-error">{{ row.error }}</span>
                                </el-tooltip>
                                <span v-else>-</span>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('sync.log.col_duration')" min-width="110px">
                            <template slot-scope="{ row }">
                                {{ durationLabel(row.started_at, row.finished_at) }}
                            </template>
                        </el-table-column>
                    </el-table>
                </div>

                <!-- ============ Pagination ============ -->
                <div class="px-4 py-3 d-flex justify-content-end align-items-center">
                    <span class="text-muted mr-3">{{ $t("sync.log.total_count") }}: {{ total }}</span>
                    <el-pagination
                        background
                        layout="sizes, prev, pager, next"
                        :total="total"
                        :page-size="per_page"
                        :page-sizes="[20, 50, 100]"
                        :current-page.sync="page"
                        @size-change="onSizeChange"
                        @current-change="onPageChange"
                    ></el-pagination>
                </div>
            </card>
        </div>
    </div>
</template>

<script>
import {
    Table,
    TableColumn,
    Select,
    Option,
    DatePicker,
    Tag,
    Tooltip,
    Pagination
} from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { mapActions } from "vuex";
import swal from "sweetalert2";
import moment from "moment";

export default {
    name: "PlatformSyncLogs",
    components: {
        RouteBreadCrumb,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [Select.name]: Select,
        [Option.name]: Option,
        [DatePicker.name]: DatePicker,
        [Tag.name]: Tag,
        [Tooltip.name]: Tooltip,
        [Pagination.name]: Pagination
    },
    data() {
        return {
            loading: false,
            filters: {
                platform: "",
                type: "",
                status: "",
                time_from: "",
                time_to: ""
            },
            rows: [],
            total: 0,
            page: 1,
            per_page: 20,
            platformOptions: [
                { value: "tiktok", label: "Tiktok" },
                { value: "lazada", label: "Lazada" },
                { value: "shopee", label: "Shopee" },
                { value: "lineshop", label: "Lineshop" },
                { value: "pancake", label: "Pancake" },
                // makro was missing here since the Mirakl round — its sync jobs
                // are written with platform "makro" so the filter could never
                // reach them.
                { value: "makro", label: "Makro PRO" },
                { value: "amaze", label: "Amaze" }
            ],
            statusOptions: ["queued", "running", "done", "failed"]
        };
    },
    mounted() {
        // Default filter window: last 7 days.
        this.filters.time_from = moment()
            .subtract(7, "days")
            .format("YYYY-MM-DD 00:00:00");
        this.filters.time_to = moment().format("YYYY-MM-DD 23:59:59");
        this.search();
    },
    methods: {
        ...mapActions({
            ListSyncJobs: "Platform/ListSyncJobs"
        }),
        queryParams() {
            const p = { page: this.page, per_page: this.per_page };
            if (this.filters.platform) p.platform = this.filters.platform;
            if (this.filters.type) p.type = this.filters.type;
            if (this.filters.status) p.status = this.filters.status;
            if (this.filters.time_from) p.time_from = this.filters.time_from;
            if (this.filters.time_to) p.time_to = this.filters.time_to;
            return p;
        },
        loadData() {
            this.loading = true;
            this.ListSyncJobs(this.queryParams())
                .then(res => {
                    const data = (res && res.data) || {};
                    this.rows = data.items || [];
                    this.total = data.total || 0;
                })
                .catch(err => {
                    console.log("list sync jobs error", err);
                    swal.fire({
                        icon: "error",
                        title: this.$t("sync.log.load_error"),
                        text: "",
                        confirmButtonClass: "btn btn-danger btn-fill",
                        buttonsStyling: false
                    });
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        search() {
            this.page = 1;
            this.loadData();
        },
        onPageChange(p) {
            this.page = p;
            this.loadData();
        },
        onSizeChange(size) {
            this.per_page = size;
            this.page = 1;
            this.loadData();
        },
        formatTime(v) {
            if (!v) return "-";
            const m = moment(v);
            return m.isValid() ? m.format("YYYY-MM-DD HH:mm:ss") : "-";
        },
        typeLabel(t) {
            if (t === "product") return this.$t("sync.log.type_product");
            if (t === "order") return this.$t("sync.log.type_order");
            return t || "-";
        },
        statusLabel(s) {
            const key = "sync.modal.status." + s;
            const label = this.$t(key);
            return label === key ? s : label;
        },
        statusTagType(s) {
            switch (s) {
                case "queued":
                    return "info";
                case "running":
                    return "warning";
                case "done":
                    return "success";
                case "failed":
                    return "danger";
                default:
                    return "info";
            }
        },
        durationLabel(start, end) {
            if (!start || !end) return "-";
            const s = moment(start);
            const e = moment(end);
            if (!s.isValid() || !e.isValid()) return "-";
            let secs = e.diff(s, "seconds");
            if (secs < 0) return "-";
            const h = Math.floor(secs / 3600);
            secs -= h * 3600;
            const m = Math.floor(secs / 60);
            secs -= m * 60;
            const parts = [];
            if (h > 0) parts.push(h + "h");
            if (m > 0) parts.push(m + "m");
            parts.push(secs + "s");
            return parts.join(" ");
        }
    }
};
</script>

<style scoped>
.sync-log-error {
    display: inline-block;
    max-width: 200px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    vertical-align: middle;
    cursor: help;
}
</style>
